import { useMutation, useQuery } from '@tanstack/react-query';
import { isServiceableSubscriptionStatus } from '@bruno/shared';
import { Badge, Button } from '@bruno/ui';
import {
  getWebsiteSubscription,
  listPlans,
  openBillingPortal,
  startSubscriptionCheckout,
} from '../api/billing';

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function statusLabel(status: string): string {
  if (status === 'past_due') return 'Payment failed';
  if (status === 'unpaid') return 'Unpaid';
  if (status === 'canceled') return 'Canceled';
  if (status === 'incomplete' || status === 'incomplete_expired') return 'Payment incomplete';
  return status;
}

export function SubscriptionPanel({ websiteId }: { websiteId: string }) {
  const plans = useQuery({ queryKey: ['plans'], queryFn: listPlans });
  const sub = useQuery({
    queryKey: ['sub', websiteId],
    queryFn: () => getWebsiteSubscription(websiteId),
  });

  const checkout = useMutation({
    mutationFn: (planId: string) => startSubscriptionCheckout({ websiteId, planId }),
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });

  const portal = useMutation({
    mutationFn: openBillingPortal,
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });

  if (sub.isLoading || plans.isLoading) return <div className="text-slate-500">Loading...</div>;

  const current = sub.data?.subscription;
  const currentPlan = sub.data?.plan;
  const serviceable = current ? isServiceableSubscriptionStatus(current.status) : false;

  return (
    <div>
      {current && serviceable ? (
        <div className="mb-4 flex items-center gap-3">
          <Badge tone="green">Active</Badge>
          <span className="font-medium">{currentPlan?.name}</span>
          {current.currentPeriodEnd ? (
            <span className="text-xs text-slate-500">
              renews {new Date(current.currentPeriodEnd).toLocaleDateString()}
            </span>
          ) : null}
        </div>
      ) : current ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="mb-1 flex items-center gap-2">
            <Badge tone="red">{statusLabel(current.status)}</Badge>
            <span className="font-medium">{currentPlan?.name}</span>
          </div>
          <p className="text-sm text-red-700">
            Bruno is paused on this website and the chat widget is hidden until payment succeeds.
            Update your payment method to re-enable it; everything resumes automatically once the
            invoice is paid.
          </p>
          <Button
            size="sm"
            variant="primary"
            className="mt-3"
            onClick={() => portal.mutate()}
            loading={portal.isPending}
          >
            Update payment method
          </Button>
        </div>
      ) : (
        <p className="text-sm text-slate-500 mb-4">
          No active subscription. Pick a plan to start using Bruno on this site.
        </p>
      )}
      {!plans.data || plans.data.plans.length === 0 ? (
        <div className="text-sm text-slate-500 italic">
          No plans available yet. Ask the Bruno admin to create plans.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          {plans.data.plans.map((p) => {
            const isCurrent = currentPlan?.id === p.id;
            return (
              <div
                key={p.id}
                className={`rounded-lg border p-4 flex flex-col ${
                  isCurrent ? 'border-indigo-500 bg-indigo-50/40' : 'border-slate-200 bg-white'
                }`}
              >
                <div className="font-semibold">{p.name}</div>
                <div className="text-2xl font-bold mt-1">
                  {dollars(p.monthlyPriceCents)}
                  <span className="text-sm text-slate-500 font-normal">/mo</span>
                </div>
                <ul className="text-xs text-slate-600 my-3 space-y-1">
                  <li>· Up to <strong>{p.maxConcurrentUsers}</strong> simultaneous end-users</li>
                  <li>· {p.includedTokens.toLocaleString()} included AI tokens / mo</li>
                  <li>· {p.maxKnowledgeMb} MB knowledge base</li>
                </ul>
                {p.description ? <p className="text-xs text-slate-500 mb-3">{p.description}</p> : null}
                <Button
                  size="sm"
                  variant={isCurrent ? 'secondary' : 'primary'}
                  disabled={isCurrent}
                  onClick={() => checkout.mutate(p.id)}
                  loading={checkout.isPending}
                >
                  {isCurrent ? 'Current plan' : current ? 'Switch to this' : 'Subscribe'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
